import {computed, makeObservable} from 'mobx';

import type {DateTimeModel} from '../Date/DateTime.model';
import type {TenantModel} from '../Tenant/Tenant.model';
import type {SubtenantArticlesAccessModel} from './SubtenantArticlesAccess.model';
import type {SubtenantExtensionConfigurationModel} from './SubtenantExtensionConfiguration.model';

import {BaseModel} from '../Base/Base.model';
import {TENANT_NOT_FOUND} from '../Tenant/constants';

const ModelName = 'SubtenantModel';

export class SubtenantModel extends BaseModel {
  articlesAccess: SubtenantArticlesAccessModel;
  coachIds: string[];
  colors: {
    onboardingBg: string;
    primary: string;
  };
  created: DateTimeModel;
  cromId?: string;
  email: {
    sender: {
      email: string;
      name: string;
    };
    signature: {
      client: string;
      coach: string;
    };
    templateFolder: string;
  };
  extensionConfiguration: SubtenantExtensionConfigurationModel;
  features: {
    autoMatching: boolean;
    digitalTwin: boolean;
    library: boolean;
    trial: {
      days: number;
      sessionsLimit: number;
    };
    waitlist: boolean;
  };
  limits: {
    conferenceBooking: number;
    maxActiveUsers: number;
    subgoalCreation: number;
    taskCreation: number;
  };
  partnerId: string;
  program: {
    duration: number;
    title: string;
  };
  session: {
    count: number;
    duration: number;
    extraLimit: number;
    intersessionBreak: number;
  };
  share: {
    access: {
      description: string;
      link: string;
    };
  };
  tenant: TenantModel;
  title: string;
  updated: DateTimeModel;
  urls: {
    faq: string;
    widget: string;
  };
  userReview: {
    secret: string;
    url: string;
  };
  widget: {
    confirmation: null | {
      sendCodeCopy: boolean;
      type: 'email' | 'phone';
    };
    isAccessible: boolean;
    uxType: 'crm' | 'full' | 'one_call';
  };

  constructor(props: SubtenantModel.Props) {
    super(props);
    Object.assign(this, props);

    makeObservable(
      this,
      BaseModel.mobxAnnotationFactory<SubtenantModel, SubtenantModel.Props>(
        props,
        {
          renderTitle: computed,
        },
      ),
    );
  }

  get isContentOnly() {
    return !this.features.autoMatching && this.articlesAccess.hasAccess;
  }

  static override get modelName(): typeof ModelName {
    return ModelName;
  }

  get modelName(): typeof ModelName {
    return ModelName;
  }

  get renderTitle() {
    let delimiter = '/';
    let tenantTitle = this.tenant.title;

    if (tenantTitle === TENANT_NOT_FOUND) {
      tenantTitle = '';
      delimiter = '';
    }

    return `${this.title}${delimiter}${tenantTitle}`;
  }
}

export namespace SubtenantModel {
  export type Props = BaseModel.ModelToProps<
    SubtenantModel,
    'isContentOnly' | 'renderTitle'
  >;
}
